import NextLink from 'next/link';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  LinearProgress,
  Link,
  Button,
} from '@mui/material';
import useInterval from 'hooks/useInterval';
import ProfileService from 'services/ProfileService';
import { UserTypeEnum } from 'enums';
import { useRouter } from 'next/router';
import { flowTrialActive } from 'utils';
import useIsMobile from 'hooks/useIsMobile';
import { COLOR_PURPLE } from 'styles/variables/colors';
import { useAuthUser } from 'features/auth/AuthUserContext';

export const useStyles = makeStyles((theme) => ({
  image: {
    maxWidth: 80,
  },
  button: {
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightBold,
    borderRadius: 40,
    backgroundColor: '#7833FF',
  },
  dialogContainer: {
    [theme.breakpoints.down('md')]: {
      backgroundColor: COLOR_PURPLE,
    },
  },
}));

const Props = {
  disableActionButton: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const fetchIntervalMs = 5000;
const maxFetchesCount = 20;

export default function SubscriptionSuccededModal({ disableActionButton, open, onClose }) {
  const classes = useStyles();
  const { user, updateUser } = useAuthUser();
  const userNotFromChinaOrJapan = user.country_code !== 'CN' && user.country_code !== 'JP';
  const isMobile = useIsMobile();
  const [isPending, setIsPending] = useState(true);
  const [fetchesCount, setFetchesCount] = useState(0);
  const profileService = new ProfileService();
  const router = useRouter();
  const { initialPath } = router.query;
  const [redirectUser, setRedirectUser] = useState(false);

  function getIconSource() {
    if (isPending) {
      return '/images/mtg-loading.gif';
    }

    return '/images/check-mark-icon-filled.svg';
  }

  function getTitle() {
    if (isPending) {
      return 'Confirming payment...';
    }

    return 'Congratulations';
  }

  function getContent() {
    if (isPending) {
      return 'We are almost there! This is the last step and will only take a few seconds...';
    }

    return "We've confirmed your subscription and now you're an upgraded user!";
  }

  function getIntervalMs() {
    return open && isPending ? fetchIntervalMs : false;
  }

  async function handleTapfiliateConversion(data) {
    const isStripeSubscription = !!data.customerId;

    if (isStripeSubscription) {
      try {
        const { data: invoice } = await profileService.getGatewayLastInvoice();

        if (data.oldPlan === UserTypeEnum.TRIAL && invoice.status === 'paid') {
          addTapfiliateConversionScript({
            customerId: data.customerId,
            chargeId: invoice.charge.id,
            total: invoice.total / 100,
            currency: invoice.currency.toUpperCase(),
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  function addTapfiliateConversionScript(data) {
    const conversionScriptElement = document.createElement('script');

    conversionScriptElement.text = `
      var options = {
        customer_id: '${data.customerId}',
        currency: '${data.currency}',
        always_callback: true,
      };
      var callback = function (error, result) {};

      (function(t,a,p){t.TapfiliateObject=a;t[a]=t[a]||function(){(t[a].q=t[a].q||[]).push(arguments)}})(window,'tap');
      tap('create', '25653-0c3e2e', {integration: 'stripe'});
      tap('conversion', '${data.chargeId}', '${data.total}', options, undefined, callback);
    `;

    document.head.appendChild(conversionScriptElement);
  }

  function handleClose() {
    if (!isPending) {
      onClose();
    }
  }

  useInterval(async () => {
    const newFetchesCount = fetchesCount + 1;

    if (newFetchesCount <= maxFetchesCount) {
      try {
        const { data } = await profileService.getUser(user.id);
        updateUser({ ...data });
        setFetchesCount(maxFetchesCount);
        setIsPending(false);
      } catch (e) {
        console.error(e);
        setFetchesCount(newFetchesCount);
      }
    } else {
      setFetchesCount(0);

      if (flowTrialActive && userNotFromChinaOrJapan && initialPath) {
        setRedirectUser(true);
        return;
      }

      setIsPending(false);
    }
  }, getIntervalMs());

  useEffect(() => {
    if (!open) {
      setIsPending(true);
    }
  }, [open]);

  useEffect(() => {
    if (isMobile) {
      router.push('/unsupported-device');
      return;
    }

    if (redirectUser) {
      router.push(initialPath);
      return;
    }
  }, [redirectUser, isPending]);

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      classes={{ container: classes.dialogContainer }}
    >
      {isPending && <LinearProgress />}

      <Box p={3} textAlign="center">
        <img className={classes.image} src={getIconSource()} alt="Feedback icon" />

        <DialogTitle>
          <b>{getTitle()}</b>
        </DialogTitle>

        <DialogContent>
          <DialogContentText>{getContent()}</DialogContentText>
        </DialogContent>

        {!isPending && (
          <>
            {!disableActionButton && (
              <NextLink href="/my-creations" passHref={true}>
                <Button className={classes.button} variant="contained" color="primary">
                  Start creating now
                </Button>
              </NextLink>
            )}

            <Box mt={!disableActionButton ? 3 : 0}>
              <Link onClick={handleClose} underline="hover">
                Close
              </Link>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}

SubscriptionSuccededModal.propTypes = Props;
